'use client';

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const DEFAULT_MESSAGES = [
  'Events, serving, and ways to get involved.',
  'Find your place to serve.',
  "See what's coming up next.",
  'Your next step starts here.',
];

const ROTATION_INTERVAL = 4000;

interface RotatingSubtitleProps {
  className?: string;
  messages?: string[];
  initialDelay?: number;
}

export function RotatingSubtitle({
  className = 'h-6',
  messages = DEFAULT_MESSAGES,
  initialDelay = 0,
}: RotatingSubtitleProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(initialDelay === 0);

  useEffect(() => {
    if (initialDelay > 0) {
      const delayTimer = setTimeout(() => {
        setIsVisible(true);
      }, initialDelay);
      return () => clearTimeout(delayTimer);
    }
  }, [initialDelay]);

  useEffect(() => {
    if (!isVisible) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % messages.length);
    }, ROTATION_INTERVAL);

    return () => clearInterval(interval);
  }, [isVisible, messages.length]);

  return (
    <div className={`relative overflow-hidden ${className}`}>
      <AnimatePresence mode="wait">
        {isVisible && (
          <motion.p
            key={currentIndex}
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -20, opacity: 0 }}
            transition={{
              y: { type: 'spring', stiffness: 300, damping: 30 },
              opacity: { duration: 0.2 },
            }}
            className="text-muted-foreground text-sm tracking-widest uppercase md:text-base"
          >
            {messages[currentIndex]}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}
